import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  InputNumber,
  Modal,
  Row,
  Select,
  Slider,
  Steps,
} from 'antd';
import { calculateCapacity } from '../APIs/intersectionEditorApi';
import { MODAL_POPUP_CLASS, NESTED_MODAL_Z_INDEX } from './modalLayer';

/** Lane types, values and labels as in the dashboard SEMAFIX editor. */
const LANE_OPTIONS = [
  ['solo_dritto', 'Solo dritto'],
  ['solo_dx', 'Solo destra'],
  ['solo_sx_con_precedenza', 'Solo sinistra con precedenza'],
  ['solo_sx_dare_precedenza', 'Solo sinistra dare precedenza'],
  ['dritto/sx', 'Dritto / sinistra'],
  ['dritto/dx', 'Dritto / destra'],
  ['dritto/dx/sx', 'Dritto / destra / sinistra'],
].map(([value, label]) => ({ value, label }));

const YES_NO_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'si', label: 'Sì' },
];

const OPPOSING_FLOW_TYPES = ['solo_sx_dare_precedenza', 'dritto/sx', 'dritto/dx/sx'];
const LEFT_TURN_TYPES = ['dritto/sx', 'dritto/dx/sx'];
const RIGHT_TURN_TYPES = ['dritto/dx', 'dritto/dx/sx'];

const INITIAL_ADVANCED = {
  capBasePerCorsia: 1200,
  larghezzaCorsia: 3.5,
  percVeicoliPesanti: 0,
  pendenza: 0,
  sensoUnicoODoppio: 'unico',
  durataParcheggio: '1',
  frequenzaFermataBus: 0,
  flussoDirezioneOpposta: 0,
  percentualeSvoltaSX: 0,
  percentualeSvoltaSXFlussoOpposto: 0,
  durataVerdeEfficace: 1,
  durataVerdeNonInConflitto: 0,
  tempoPrimaDiSvoltaSX: 0,
  percentualeSvoltaDX: 0,
};

function NumberField({ label, value, onChange, min, max, step }) {
  return (
    <Col xs={24} sm={12}>
      <label className="semafix-editor-field">
        <span>{label}</span>
        <InputNumber
          value={value}
          onChange={(next) => onChange(next ?? 0)}
          min={min}
          max={max}
          step={step}
        />
      </label>
    </Col>
  );
}

export default function CapacityCalculatorModal({ open, onCancel, onCalculated }) {
  const [step, setStep] = useState(0);
  const [laneCount, setLaneCount] = useState(1);
  const [parking, setParking] = useState('no');
  const [busStop, setBusStop] = useState('no');
  const [laneTypes, setLaneTypes] = useState(['solo_dritto']);
  const [advanced, setAdvanced] = useState(INITIAL_ADVANCED);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const hasParking = parking === 'si';
  const hasBusStop = busStop === 'si';
  const hasOpposingFlow = useMemo(
    () => laneTypes.some((type) => OPPOSING_FLOW_TYPES.includes(type)),
    [laneTypes],
  );
  const hasLeftTurn = laneTypes.some((type) => LEFT_TURN_TYPES.includes(type));
  const hasRightTurn = laneTypes.some((type) => RIGHT_TURN_TYPES.includes(type));

  const setAdvancedField = (field, value) => {
    setAdvanced((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const reset = () => {
    setStep(0);
    setLaneCount(1);
    setParking('no');
    setBusStop('no');
    setLaneTypes(['solo_dritto']);
    setAdvanced(INITIAL_ADVANCED);
    setResults(null);
    setError('');
  };

  const continueFromBase = () => {
    setLaneTypes((current) => Array.from(
      { length: laneCount },
      (_, index) => current[index] || 'solo_dritto',
    ));
    setStep(1);
  };

  const isValidConfiguration = () => {
    if (hasOpposingFlow && !(Number(advanced.flussoDirezioneOpposta) > 0)) return false;
    return Number(advanced.percentualeSvoltaSX) + Number(advanced.percentualeSvoltaDX) <= 100;
  };

  const runCalculation = async () => {
    if (!isValidConfiguration()) {
      setError('⚠️ Dati incompleti: assicurati di aver inserito un valore positivo per il '
        + 'Flusso Direzione Opposta e che la somma delle svolte a SX e DX non superi il 100%.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await calculateCapacity({
        parametri_manovre: [{
          num_corsie: laneCount,
          cap_base_per_corsia: advanced.capBasePerCorsia,
          larghezza_corsia: advanced.larghezzaCorsia,
          perc_veicoli_pesanti: advanced.percVeicoliPesanti,
          pendenza: advanced.pendenza,
          tipo_manovra: laneTypes,
          senso_unico_o_doppio: hasParking ? advanced.sensoUnicoODoppio : null,
          durata_parcheggio: hasParking ? advanced.durataParcheggio : null,
          frequenza_fermata_bus: hasBusStop ? advanced.frequenzaFermataBus : null,
          flusso_direzione_opposta: advanced.flussoDirezioneOpposta,
          '%_svolta_sx': advanced.percentualeSvoltaSX,
          '%_svolta_sx_flusso_opposto': advanced.percentualeSvoltaSXFlussoOpposto,
          durata_verde_efficace: advanced.durataVerdeEfficace,
          durata_verde_non_in_conflitto: advanced.durataVerdeNonInConflitto,
          tempo_prima_di_svolta_sx: advanced.tempoPrimaDiSvoltaSX,
          '%_svolta_dx': advanced.percentualeSvoltaDX,
        }],
        presenza_parcheggi: [parking],
        presenza_fermate_bus: [busStop],
      });
      if (response?.error) throw new Error(response.error);
      const values = Array.isArray(response?.result)
        ? response.result
        : Array.isArray(response) ? response : [];
      const messages = Array.isArray(response?.errors) ? response.errors : [];
      const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
      setResults({ values, messages, total });
      onCalculated(total, {
        laneCount, parking, busStop, laneTypes, advanced, response,
      });
    } catch (requestError) {
      setError(requestError.message || 'Calcolo della capacità non riuscito.');
    } finally {
      setLoading(false);
    }
  };

  const footer = [
    results && <Button key="recalculate" onClick={() => setResults(null)}>Ricalcola</Button>,
    <Button key="reset" danger onClick={reset}>Reset</Button>,
    step > 0 && <Button key="back" onClick={() => setStep((value) => value - 1)}>Indietro</Button>,
    step === 0 && <Button key="base" type="primary" onClick={continueFromBase}>Conferma manovre</Button>,
    step === 1 && <Button key="lanes" type="primary" onClick={() => setStep(2)}>Conferma tipo di corsie</Button>,
    step === 2 && (
      <Button
        key="calculate"
        type="primary"
        loading={loading}
        disabled={Boolean(results)}
        onClick={runCalculation}
      >
        Calcola capacità
      </Button>
    ),
    <Button key="close" onClick={onCancel}>Chiudi</Button>,
  ].filter(Boolean);

  return (
    <Modal
      className="semafix-modal"
      open={open}
      title="Calcola capacità di saturazione"
      width={880}
      zIndex={NESTED_MODAL_Z_INDEX}
      onCancel={onCancel}
      footer={footer}
    >
      <Steps
        size="small"
        current={step}
        items={[
          { title: 'Configura la manovra' },
          { title: 'Tipo di corsia' },
          { title: 'Parametri avanzati' },
        ]}
      />
      {error && <Alert className="semafix-editor-alert" type="error" showIcon message={error} />}

      {step === 0 && (
        <div className="semafix-editor-form-section">
          <p className="semafix-editor-form-legend">Configura la manovra</p>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label className="semafix-editor-field">
                <span>Numero corsie</span>
                <Row gutter={12} align="middle">
                  <Col flex="auto">
                    <Slider min={1} max={5} value={laneCount} onChange={setLaneCount} />
                  </Col>
                  <Col flex="none">
                    <InputNumber
                      min={1}
                      max={5}
                      value={laneCount}
                      onChange={(value) => setLaneCount(value ?? 1)}
                    />
                  </Col>
                </Row>
              </label>
            </Col>
            <Col xs={24} sm={12}>
              <label className="semafix-editor-field">
                <span>Parcheggi</span>
                <Select
                  value={parking}
                  options={YES_NO_OPTIONS}
                  popupClassName={MODAL_POPUP_CLASS}
                  onChange={setParking}
                />
              </label>
            </Col>
            <Col xs={24} sm={12}>
              <label className="semafix-editor-field">
                <span>Fermate bus</span>
                <Select
                  value={busStop}
                  options={YES_NO_OPTIONS}
                  popupClassName={MODAL_POPUP_CLASS}
                  onChange={setBusStop}
                />
              </label>
            </Col>
          </Row>
        </div>
      )}

      {step === 1 && (
        <div className="semafix-editor-form-section">
          <p className="semafix-editor-form-legend">Definisci il tipo di corsia</p>
          <Row gutter={[16, 16]}>
            {laneTypes.map((type, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Col xs={24} sm={12} key={`lane-${index}`}>
                <label className="semafix-editor-field">
                  <span>Tipo corsia {index + 1}</span>
                  <Select
                    value={type}
                    options={LANE_OPTIONS}
                    popupClassName={MODAL_POPUP_CLASS}
                    onChange={(value) => setLaneTypes((current) => current.map(
                      (item, itemIndex) => (itemIndex === index ? value : item),
                    ))}
                  />
                </label>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {step === 2 && (
        <div className="semafix-editor-form-section">
          <p className="semafix-editor-form-legend">Parametri avanzati</p>
          <Row gutter={[16, 16]}>
            <NumberField
              label="Capacità base per corsia (veicoli/ora)"
              value={advanced.capBasePerCorsia}
              onChange={(value) => setAdvancedField('capBasePerCorsia', value)}
            />
            <NumberField
              label="Larghezza corsia (metri)"
              value={advanced.larghezzaCorsia}
              step={0.1}
              onChange={(value) => setAdvancedField('larghezzaCorsia', value)}
            />
            <NumberField
              label="Percentuale veicoli pesanti (%)"
              value={advanced.percVeicoliPesanti}
              min={0}
              max={100}
              onChange={(value) => setAdvancedField('percVeicoliPesanti', value)}
            />
            <NumberField
              label="Pendenza (%)"
              value={advanced.pendenza}
              onChange={(value) => setAdvancedField('pendenza', value)}
            />
            {hasParking && (
              <>
                <Col xs={24} sm={12}>
                  <label className="semafix-editor-field">
                    <span>Senso unico o doppio</span>
                    <Select
                      value={advanced.sensoUnicoODoppio}
                      options={[
                        { value: 'unico', label: 'Unico' },
                        { value: 'doppio', label: 'Doppio' },
                      ]}
                      popupClassName={MODAL_POPUP_CLASS}
                      onChange={(value) => setAdvancedField('sensoUnicoODoppio', value)}
                    />
                  </label>
                </Col>
                <Col xs={24} sm={12}>
                  <label className="semafix-editor-field">
                    <span>Durata parcheggio (ore)</span>
                    <Select
                      value={advanced.durataParcheggio}
                      options={[{ value: '1', label: '1' }, { value: '2', label: '2' }]}
                      popupClassName={MODAL_POPUP_CLASS}
                      onChange={(value) => setAdvancedField('durataParcheggio', value)}
                    />
                  </label>
                </Col>
              </>
            )}
            {hasBusStop && (
              <NumberField
                label="Frequenza fermata bus (bus/ora)"
                value={advanced.frequenzaFermataBus}
                min={0}
                onChange={(value) => setAdvancedField('frequenzaFermataBus', value)}
              />
            )}
            {hasOpposingFlow && (
              <NumberField
                label="Flusso direzione opposta (veicoli/ora)"
                value={advanced.flussoDirezioneOpposta}
                min={0}
                onChange={(value) => setAdvancedField('flussoDirezioneOpposta', value)}
              />
            )}
            {hasLeftTurn && (
              <>
                <NumberField
                  label="Veicoli che svoltano a SX (%)"
                  value={advanced.percentualeSvoltaSX}
                  min={0}
                  max={100}
                  onChange={(value) => setAdvancedField('percentualeSvoltaSX', value)}
                />
                <NumberField
                  label="Veicoli che svoltano a SX nel flusso opposto (%)"
                  value={advanced.percentualeSvoltaSXFlussoOpposto}
                  min={0}
                  max={100}
                  onChange={(value) => setAdvancedField('percentualeSvoltaSXFlussoOpposto', value)}
                />
                <NumberField
                  label="Durata verde efficace (s)"
                  value={advanced.durataVerdeEfficace}
                  min={0}
                  onChange={(value) => setAdvancedField('durataVerdeEfficace', value)}
                />
                <NumberField
                  label="Durata verde non in conflitto (s)"
                  value={advanced.durataVerdeNonInConflitto}
                  min={0}
                  onChange={(value) => setAdvancedField('durataVerdeNonInConflitto', value)}
                />
                <NumberField
                  label="Tempo prima di svolta a SX (s)"
                  value={advanced.tempoPrimaDiSvoltaSX}
                  min={0}
                  onChange={(value) => setAdvancedField('tempoPrimaDiSvoltaSX', value)}
                />
              </>
            )}
            {hasRightTurn && (
              <NumberField
                label="Veicoli che svoltano a DX (%)"
                value={advanced.percentualeSvoltaDX}
                min={0}
                max={100}
                onChange={(value) => setAdvancedField('percentualeSvoltaDX', value)}
              />
            )}
          </Row>
        </div>
      )}

      <div className="semafix-capacity-results">
        <h4>Risultati</h4>
        {results
          ? (
            <>
              {results.values.map((value, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`capacity-${index}`}>
                  Manovra {index + 1}: {Number(value || 0).toLocaleString('it-IT')} veicoli/ora
                </div>
              ))}
              {results.messages.length > 0 && (
                <>
                  <h4 style={{ marginTop: 10 }}>Messaggi</h4>
                  <ul>
                    {results.messages.map((text) => <li key={text}>{text}</li>)}
                  </ul>
                </>
              )}
              <b className="semafix-capacity-total">
                CapSat totale: {results.total.toLocaleString('it-IT')} veicoli/ora
              </b>
            </>
          )
          : <span>Nessun risultato ancora.</span>}
      </div>
    </Modal>
  );
}
